import type { ReactNode } from 'react'
import type { StockDetailData } from '@/lib/market/builders/stock'
import type { RiskStats } from '@/lib/market/builders/shared'
import type { ChartPane } from '@/components/charts/time-chart'

import { useCallback, useMemo, useState } from 'react'
import { cn } from '@/lib/utils'
import { dayTicks } from '@/components/charts/scales'
import { Segmented } from '@/components/market/segmented'
import { formatDay } from '@/lib/market/calendar'
import { formatNumber, formatPercent } from '@/lib/market/format'
import Histogram from '@/components/charts/histogram'
import ScatterPlot from '@/components/charts/scatter-plot'
import TimeChart from '@/components/charts/time-chart'

type StatWindow = 'y1' | 'y3'

interface StatTileDef {
    label: string
    value: (stats: RiskStats) => string
    tone?: (stats: RiskStats) => number | null
    help: string
}

export const RISK_TILES: StatTileDef[] = [
    { label: 'Annualised return', value: stats => formatPercent(stats.annualReturn, 1), tone: stats => stats.annualReturn, help: 'Average daily log return scaled to a year.' },
    { label: 'Volatility', value: stats => formatPercent(stats.volatility, 1, false), help: 'Standard deviation of daily returns, scaled by the square root of 252.' },
    { label: 'Sharpe ratio', value: stats => formatNumber(stats.sharpe, 2), tone: stats => stats.sharpe, help: 'Excess return over the 91-day bill rate per unit of volatility.' },
    { label: 'Sortino ratio', value: stats => formatNumber(stats.sortino, 2), tone: stats => stats.sortino, help: 'Excess return per unit of downside volatility only.' },
    { label: 'Beta to KASI', value: stats => (stats.beta !== null ? formatNumber(stats.beta, 2) : '-'), help: 'How much the share moves for a 1 percent move in the market.' },
    { label: 'Alpha', value: stats => (stats.alpha !== null ? formatPercent(stats.alpha, 1) : '-'), tone: stats => stats.alpha, help: 'Annual return left after adjusting for market exposure.' },
    { label: 'Correlation', value: stats => (stats.correlation !== null ? formatNumber(stats.correlation, 2) : '-'), help: 'Correlation of daily returns with the KASI, from -1 to 1.' },
    { label: 'Maximum drawdown', value: stats => formatPercent(stats.maxDrawdown, 1), tone: stats => stats.maxDrawdown, help: 'Largest fall from a peak to a later trough.' },
    { label: 'Value at risk, 95%', value: stats => formatPercent(-stats.var95, 2), help: 'A daily loss exceeded on only 1 session in 20.' },
    { label: 'Expected shortfall', value: stats => formatPercent(-stats.cvar95, 2), help: 'Average loss on the worst 5 percent of sessions.' },
    { label: 'Skewness', value: stats => formatNumber(stats.skewness, 2), help: 'Asymmetry of returns. Negative means more large down days.' },
    { label: 'Excess kurtosis', value: stats => formatNumber(stats.kurtosis, 2), help: 'Fat tails compared with a normal distribution.' },
    { label: 'Positive sessions', value: stats => formatPercent(stats.upDays, 1, false), help: 'Share of sessions that closed higher.' },
    { label: 'Best session', value: stats => formatPercent(stats.bestDay, 2), tone: stats => stats.bestDay, help: 'Largest one-day gain in the window.' },
    { label: 'Worst session', value: stats => formatPercent(stats.worstDay, 2), tone: stats => stats.worstDay, help: 'Largest one-day loss in the window.' }
]

export const RiskTiles = ({ stats, className }: { stats: RiskStats, className?: string }): ReactNode => {
    return (
        <dl className={cn('grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-border bg-border sm:grid-cols-3 xl:grid-cols-5', className)}>
            {RISK_TILES.map(tile => {
                const tone = tile.tone ? tile.tone(stats) : null

                return (
                    <div key={tile.label} className='bg-card px-3.5 py-3' title={tile.help}>
                        <dt className='text-[11px] text-muted-foreground'>{tile.label}</dt>
                        <dd className={cn('mt-0.5 text-[15px] font-semibold', tone === null ? 'text-foreground' : tone > 0 ? 'text-up' : tone < 0 ? 'text-down' : 'text-foreground')}>
                            {tile.value(stats)}
                        </dd>
                        <dd className='mt-1 hidden text-[11px] leading-snug text-muted-foreground lg:block'>{tile.help}</dd>
                    </div>
                )
            })}
        </dl>
    )
}

interface StockStatisticsProps {
    ticker: string
    data: StockDetailData
}

const StockStatistics = ({ ticker, data }: StockStatisticsProps): ReactNode => {
    const [statWindow, setStatWindow] = useState<StatWindow>('y1')
    const stats = data.stats[statWindow]

    const rollingLabels = useMemo(() => data.rolling.d.map(day => formatDay(day)), [data.rolling.d])
    const rollingTicks = useCallback((width: number) => dayTicks(data.rolling.d, width), [data.rolling.d])

    const rollingPanes: ChartPane[] = useMemo(() => [{
        id: 'rolling',
        height: 170,
        format: value => `${formatNumber(value, 0)}%`,
        series: [{ kind: 'line', id: 'rolling', label: '21-session volatility', color: 'var(--series-1)', values: data.rolling.v, format: value => formatPercent(value, 1, false) }]
    }], [data.rolling.v])

    const drawdownLabels = useMemo(() => data.drawdown.d.map(day => formatDay(day)), [data.drawdown.d])
    const drawdownTicks = useCallback((width: number) => dayTicks(data.drawdown.d, width), [data.drawdown.d])

    const drawdownPanes: ChartPane[] = useMemo(() => [{
        id: 'drawdown',
        height: 170,
        format: value => `${formatNumber(value, 0)}%`,
        series: [{ kind: 'area', id: 'drawdown', label: 'Below previous peak', color: 'var(--series-8)', values: data.drawdown.v, format: value => formatPercent(value, 1) }]
    }], [data.drawdown.v])

    const scatterPoints = useMemo(() => data.scatter.points.map(([x, y], index) => ({ key: String(index), x, y })), [data.scatter.points])

    return (
        <div className='space-y-6'>
            <div className='flex flex-wrap items-center justify-between gap-3'>
                <p className='text-[13px] text-muted-foreground'>
                    {stats.observations} daily returns, risk-free rate taken as the 91-day bill.
                </p>
                <Segmented label='Statistics window' options={[{ value: 'y1', label: '12 months' }, { value: 'y3', label: '3 years' }]} value={statWindow} onChange={setStatWindow} />
            </div>

            <RiskTiles stats={stats} />

            <div className='grid gap-6 lg:grid-cols-2'>
                <figure>
                    <figcaption className='mb-2'>
                        <span className='block text-sm font-medium text-foreground'>Distribution of daily returns, 12 months</span>
                        <span className='block text-xs text-muted-foreground'>
                            Mean {formatPercent(data.distribution.mean, 3)}, standard deviation {formatPercent(data.distribution.std, 2, false)}.
                            {data.distribution.outside > 0 ? ` ${data.distribution.outside} extreme sessions fall outside the chart.` : ''}
                        </span>
                    </figcaption>
                    <Histogram
                        edges={data.distribution.edges}
                        counts={data.distribution.counts}
                        curve={data.distribution.curve}
                        ariaLabel={`Histogram of ${ticker} daily returns over 12 months`}
                    />
                </figure>

                <figure>
                    <figcaption className='mb-2'>
                        <span className='block text-sm font-medium text-foreground'>{ticker} against the market, daily returns</span>
                        <span className='block text-xs text-muted-foreground'>
                            Slope of the fitted line is the beta: {formatNumber(data.scatter.slope, 2)}. R squared {formatNumber(data.scatter.r2, 2)}.
                        </span>
                    </figcaption>
                    <ScatterPlot
                        points={scatterPoints}
                        height={248}
                        xLabel='KASI daily return'
                        yLabel={`${ticker} daily return`}
                        xFormat={value => `${formatNumber(value, 1)}%`}
                        yFormat={value => `${formatNumber(value, 1)}%`}
                        regression={{ slope: data.scatter.slope, intercept: data.scatter.intercept }}
                        ariaLabel={`Scatter of ${ticker} daily returns against KASI daily returns with a regression line`}
                    />
                </figure>

                <figure>
                    <figcaption className='mb-2'>
                        <span className='block text-sm font-medium text-foreground'>Rolling volatility</span>
                        <span className='block text-xs text-muted-foreground'>Annualised, measured over the previous 21 sessions.</span>
                    </figcaption>
                    <TimeChart count={data.rolling.d.length} labels={rollingLabels} xTicks={rollingTicks} panes={rollingPanes} ariaLabel={`${ticker} rolling 21-session volatility over 12 months`} />
                </figure>

                <figure>
                    <figcaption className='mb-2'>
                        <span className='block text-sm font-medium text-foreground'>Drawdown from peak</span>
                        <span className='block text-xs text-muted-foreground'>How far the price sat below its previous high, 12 months.</span>
                    </figcaption>
                    <TimeChart count={data.drawdown.d.length} labels={drawdownLabels} xTicks={drawdownTicks} panes={drawdownPanes} ariaLabel={`${ticker} drawdown from previous peak over 12 months`} />
                </figure>
            </div>
        </div>
    )
}

export default StockStatistics
